import React, { useMemo, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  SpotAssetAllocationChart,
  SpotCumulativePnlChart,
  SpotDailyPnlChart,
} from "./SpotPnlCharts";
import useSpotPnlAnalysis from "./useSpotPnlAnalysis";
import "../FundingAccountPage/fundingAccount.css";
import "../SpotOrders/spotOrders.css";
import "../OptionsAccountPage/optionsPnlAnalysis.css";
import "./spotPnlAnalysis.css";

function num(v) {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function fmt(val, decimals = 4) {
  const n = num(val);
  return parseFloat(n.toFixed(decimals)).toString();
}

function fmtSigned(val, decimals = 4) {
  const n = num(val);
  const abs = fmt(Math.abs(n), decimals);
  if (n > 0) return `+${abs}`;
  if (n < 0) return `-${abs}`;
  return abs;
}

function fmtUsd(val, decimals = 2) {
  const n = num(val);
  return `$ ${Math.abs(n).toFixed(decimals)}`;
}

function pnlClass(val) {
  const n = num(val);
  if (n > 0) return "options-pnl-pos";
  if (n < 0) return "options-pnl-neg";
  return "options-pnl-neutral";
}

function DatePickerCell({ value, onChange, ariaLabel, inputRef }) {
  return (
    <div className="so-dateCell">
      <span className="so-dateCell__text" aria-hidden="true">
        {value || "—"}
      </span>
      <input
        ref={inputRef}
        type="date"
        className="so-dateCell__native"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={ariaLabel}
      />
    </div>
  );
}

function SummaryCard({ label, card, sub, showValues }) {
  const pnl = num(card?.pnl_usdt);
  const pct = num(card?.pnl_pct);
  return (
    <div className="funding-account__summarycard options-pnl-metric-card spot-pnl-metric-card">
      <div className="options-pnl-card-k">{label}</div>
      {sub ? (
        <>
          <div className="spot-pnl-balance-main">{showValues ? sub : "****"}</div>
          {card?.secondary ? (
            <div className="spot-pnl-balance-sub">{showValues ? card.secondary : "****"}</div>
          ) : null}
        </>
      ) : (
        <>
          <div className={`options-pnl-card-v ${pnlClass(pnl)}`}>
            {showValues ? `${fmtSigned(pct, 2)}%` : "****"}
          </div>
          <div className={`options-pnl-card-sub ${pnlClass(pnl)}`}>
            {showValues ? `${fmtSigned(pnl, 4)} USDT` : "****"}
          </div>
        </>
      )}
      <div className="options-pnl-card-foot" />
    </div>
  );
}

export default function SpotPnlAnalysisPage() {
  const navigate = useNavigate();
  const [showBalance, setShowBalance] = useState(true);
  const [viewTab, setViewTab] = useState("overview");
  const fromRef = useRef(null);
  const toRef = useRef(null);

  const token = useMemo(() => {
    try {
      return localStorage.getItem("token") || sessionStorage.getItem("token") || "";
    } catch {
      return "";
    }
  }, []);

  const {
    period,
    dateFrom,
    dateTo,
    analysis,
    detailsRows,
    detailsPagination,
    detailsPage,
    loading,
    detailsLoading,
    error,
    usingFallback,
    applyPeriod,
    setCustomRange,
    setDetailsPage,
  } = useSpotPnlAnalysis({ token, enabled: Boolean(token) });

  const cards = analysis?.summary_cards;
  const chart = analysis?.chart;
  const summary = analysis?.range_summary;
  const assetAllocation = analysis?.asset_allocation ?? [];
  const detailsPages = detailsPagination?.total_pages ?? 1;

  const estimatedBalance = useMemo(() => {
    const bal = analysis?.estimated_balance;
    const usdt = num(bal?.usdt);
    const btc = num(bal?.btc);
    return {
      btc: btc > 0 ? fmt(btc, 8) : null,
      usdt,
      secondary: usdt > 0 ? fmtUsd(usdt) : null,
    };
  }, [analysis?.estimated_balance]);

  const openDatePicker = (ref) => {
    const el = ref?.current;
    if (!el) return;
    if (typeof el.showPicker === "function") el.showPicker();
    else el.focus();
  };

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="dashboard_right funding-account margin-pro-page options-pnl-page spot-pnl-page">
      <button type="button" className="options-pnl-back" onClick={() => navigate("/user_profile/wallet/spot")}>
        <i className="ri-arrow-left-s-line" />Spot Wallet
      </button>

      <div className="funding-account__header">
        <h1 className="funding-account__title options-pnl-page-title mb-0">
          Spot Profit &amp; Loss Analysis
          <button
            type="button"
            className="options-pnl-visibility-btn"
            onClick={() => setShowBalance((v) => !v)}
            aria-label={showBalance ? "Hide balances" : "Show balances"}
          >
            <i className={showBalance ? "ri-eye-line" : "ri-eye-off-line"} aria-hidden="true" />
          </button>
        </h1>
      </div>

      {error && (
        <div className="alert alert-danger py-2 px-3 mb-3" role="alert">
          {error}
        </div>
      )}

      {usingFallback && !error && (
        <div className="alert alert-secondary py-2 px-3 mb-3 spot-pnl-fallback-note" role="status">
          Account analysis unavailable — showing trade PnL from GET /me/pnl. Charts require GET /me/pnl/analysis.
        </div>
      )}

      <div className="options-pnl-cards spot-pnl-top-cards">
        <SummaryCard
          label="Estimated Balance"
          card={{ secondary: estimatedBalance.secondary }}
          sub={showBalance
            ? (estimatedBalance.btc
              ? `${estimatedBalance.btc} BTC`
              : `${fmt(estimatedBalance.usdt, 4)} USDT`)
            : "****"}
          showValues={showBalance}
        />
        <SummaryCard
          label="Today's PNL"
          card={cards?.today ?? cards?.daily}
          showValues={showBalance}
        />
        <SummaryCard
          label="30D PNL"
          card={cards?.["30d"]}
          showValues={showBalance}
        />
      </div>

      <div className="funding-account__summarycard options-pnl-main-panel">
        <div className="options-pnl-toolbar">
          <div className="cmp-pnl-btn-group">
            {["7d", "30d", "90d"].map((p) => (
              <button
                key={p}
                type="button"
                className={`cmp-pnl-btn ${period === p ? "active" : ""}`}
                onClick={() => applyPeriod(p)}
              >
                Last {p === "7d" ? "7 days" : p === "30d" ? "30 days" : "90 days"}
              </button>
            ))}
          </div>
          <div className="options-pnl-date-filter">
            <div className="so-dateRow">
              <DatePickerCell
                value={dateFrom}
                onChange={(v) => setCustomRange(v, null)}
                ariaLabel="Start date"
                inputRef={fromRef}
              />
              <span className="so-dateArrow" aria-hidden="true">→</span>
              <DatePickerCell
                value={dateTo}
                onChange={(v) => setCustomRange(null, v)}
                ariaLabel="End date"
                inputRef={toRef}
              />
              <button
                type="button"
                className="so-dateCalBtn"
                aria-label="Open calendar"
                onClick={() => openDatePicker(fromRef)}
              >
                <i className="ri-calendar-line" />
              </button>
            </div>
          </div>
        </div>

        <div className="options-pnl-section-title">Profit and Loss Summary</div>
        {loading ? (
          <div className="cmp-loading-text py-3">Loading…</div>
        ) : (
          <div className="options-pnl-summary-grid">
            <div>
              {[
                ["Total Profit", summary?.total_profit],
                ["Total Loss", summary?.total_loss],
                ["Net PNL", summary?.net_pnl],
              ].map(([label, value]) => (
                <div className="options-pnl-summary-row" key={label}>
                  <span>{label}</span>
                  <span className={showBalance ? pnlClass(value) : ""}>
                    {showBalance ? `${fmtSigned(value, 4)} USDT` : "****"}
                  </span>
                </div>
              ))}
            </div>
            <div>
              {[
                ["Win Rate", summary?.win_rate_pct, true],
                ["Winning Days", summary?.winning_days, false],
                ["Losing Days", summary?.losing_days, false],
                ["Breakeven Days", summary?.breakeven_days, false],
              ].map(([label, value, isPct]) => (
                <div className="options-pnl-summary-row" key={label}>
                  <span>{label}</span>
                  <span>
                    {showBalance
                      ? (isPct ? `${fmt(value, 2)}%` : String(value ?? 0))
                      : "****"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="cmp-tabs-header mt-3">
          <div className="cmp-tabs-group">
            {["overview", "details"].map((tab) => (
              <button
                key={tab}
                type="button"
                className={`cmp-tab-btn ${viewTab === tab ? "active" : ""}`}
                onClick={() => setViewTab(tab)}
              >
                {tab === "overview" ? "Overview" : "Details"}
              </button>
            ))}
          </div>
        </div>

        {viewTab === "overview" && !loading && (
          <div className="spot-pnl-charts-grid">
            <div className="row g-3 options-pnl-charts-row">
              <div className="col-lg-6">
                <SpotCumulativePnlChart
                  usdtData={chart?.cumulative_pnl_usdt}
                  pctData={chart?.cumulative_pnl_pct}
                />
              </div>
              <div className="col-lg-6">
                <SpotAssetAllocationChart data={assetAllocation} />
              </div>
            </div>
            <div className="row g-3 options-pnl-charts-row">
              <div className="col-lg-6">
                <SpotDailyPnlChart data={chart?.daily_pnl ?? chart?.daily_account_pnl} title="Daily PNL" />
              </div>
            </div>
          </div>
        )}

        {viewTab === "details" && (
          <>
            <div className="funding-account__tablewrap mt-3">
              <table className="funding-account__table funding-account__table--futures">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th className="ta-r">Daily Profit and Loss</th>
                    <th className="ta-r">Cumulative Profit and Loss</th>
                    <th className="ta-r">Cumulative Profit and Loss %</th>
                    <th className="ta-r">Net Transfer (USDT)</th>
                  </tr>
                </thead>
                <tbody>
                  {loading || detailsLoading ? (
                    <tr><td colSpan={5} className="text-center py-4 text-muted">Loading…</td></tr>
                  ) : detailsRows.length === 0 ? (
                    <tr><td colSpan={5} className="text-center py-4 text-muted">No data</td></tr>
                  ) : detailsRows.map((row) => {
                    const daily = num(row.daily_pnl ?? row.daily_profit_and_loss);
                    const cum = num(row.cumulative_pnl ?? row.cumulative_profit_and_loss);
                    const cumPct = num(row.cumulative_pnl_pct ?? row.cumulative_profit_and_loss_pct);
                    const netTransfer = num(row.net_transfer_usdt);
                    return (
                      <tr key={row.date}>
                        <td>{row.date}</td>
                        <td className={`ta-r ${showBalance ? pnlClass(daily) : ""}`}>
                          {showBalance ? fmtUsd(daily) : "****"}
                        </td>
                        <td className={`ta-r ${showBalance ? pnlClass(cum) : ""}`}>
                          {showBalance ? fmtUsd(cum) : "****"}
                        </td>
                        <td className={`ta-r ${showBalance ? pnlClass(cumPct) : ""}`}>
                          {showBalance ? `${fmtSigned(cumPct, 2)}%` : "****"}
                        </td>
                        <td className={`ta-r ${showBalance ? pnlClass(netTransfer) : ""}`}>
                          {showBalance ? `${fmtSigned(netTransfer, 4)} USDT` : "****"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {detailsPages > 1 && (
              <div className="d-flex justify-content-end gap-2 mt-3">
                <button
                  type="button"
                  className="funding-account__pill"
                  disabled={detailsPage <= 1}
                  onClick={() => setDetailsPage((p) => Math.max(1, p - 1))}
                >
                  ‹
                </button>
                <span className="align-self-center text-muted small">Page {detailsPage} / {detailsPages}</span>
                <button
                  type="button"
                  className="funding-account__pill"
                  disabled={detailsPage >= detailsPages}
                  onClick={() => setDetailsPage((p) => p + 1)}
                >
                  ›
                </button>
              </div>
            )}
          </>
        )}

        <p className="spot-pnl-footnote mt-3 mb-0">
          Data update time refers to UTC+0. Data maintenance occurs daily between 0:00 and 2:00 (UTC+0).
          {analysis?.meta?.note ? ` ${analysis.meta.note}` : " Data is for reference only due to the complexity of financial data."}
        </p>
      </div>
    </div>
  );
}
